import { PaymentService } from '@/api'
import { Input } from '@/cn/ui/input'
import { useToast } from '@/cn/ui/use-toast'
import { rpcProvider } from '@/config/rpcProvider'
import { CHAIN_ID } from '@/constants'
import { TitleBar } from '@/ui/titlebar'
import { handleQrScan } from '@/utils'
import { NativeBridge } from '@/utils/native_bridge'
import { ethers } from 'ethers'
import { Clipboard, QrCode } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PeopleSearch from './people_search_component'

const CRAY_ID_RE = /^[a-z0-9._-]+@cray$/i

const SendAnyone = () => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const [value, setValue] = useState('')
  const [resolving, setResolving] = useState(false)
  const didNavigate = useRef(false)
  const debTimer = useRef<number | null>(null)

  const resetNavGuard = () => {
    didNavigate.current = false
  }

  const resolveAndGo = async (input: string) => {
    const t = input.trim()
    console.log('resolving', { t, resolving, didNavigate: didNavigate.current })
    if (!t || resolving || didNavigate.current) return

    try {
      setResolving(true)

      // 1) Direct address
      if (ethers.isAddress(t)) {
        const user = await PaymentService.GetUserAddressByAddress(t)
        if (user?.result?.user) {
          didNavigate.current = true
          navigate('/dashboard/send-money', {
            state: {
              address: user.result.user.address,
              username: user.result.user.cpiid,
              name: user.result.user.name,
              userId: user.result.user.userId,
            },
          })
        } else {
          didNavigate.current = true
          navigate('/dashboard/send-money', { state: { address: t } })
        }
        return
      }

      // 2) ENS
      if ((t as string).endsWith('.eth')) {
        const p = rpcProvider[CHAIN_ID.MAINNET]
        // @ts-ignore
        const address = await p.resolveName(t)
        if (!address) {
          toast({ variant: 'destructive', title: "Couldn't resolve that ENS" })
          return
        }
        didNavigate.current = true
        navigate('/dashboard/send-money', { state: { ens: t, address } })
        return
      }

      // 3) Cray ID
      if (CRAY_ID_RE.test(t)) {
        const crayId = (t as any).replace(/@cray$/i, '')
        const {
          result: { user },
        } = await PaymentService.GetUserAddressByCPID(crayId)
        didNavigate.current = true
        console.log('got user', user)
        if (user) {
          didNavigate.current = true
          navigate('/dashboard/send-money', {
            state: {
              address: user.address,
              username: user.cpiid,
              name: user.name,
              userId: user.userId,
            },
          })
        } else {
          didNavigate.current = true
          navigate('/dashboard/send-money', { state: { address: t } })
        }
        return
      }

      // 4) Otherwise: let PeopleSearch handle names (do nothing here)
    } catch (err) {
      console.error(err)
      toast({ variant: 'destructive', title: 'Something went wrong, please try again' })
    } finally {
      setResolving(false)
    }
  }

  // Debounced auto-resolve on input change
  useEffect(() => {
    if (debTimer.current) window.clearTimeout(debTimer.current)
    debTimer.current = window.setTimeout(() => resolveAndGo(value), 300)
    return () => {
      if (debTimer.current) window.clearTimeout(debTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Paste helper
  const handlePaste = async () => {
    try {
      let text = ''
      if (NativeBridge.isBridge()) {
        text = (await NativeBridge.Send('PASTE')) as string
      } else {
        text = (await navigator.clipboard.readText())?.trim()
      }
      if (text) {
        resetNavGuard()
        setValue(text)
        // immediate resolve (don’t wait for debounce on paste)
        resolveAndGo(text)
      }
    } catch {
      toast({ variant: 'destructive', title: 'Could not paste from clipboard' })
    }
  }

  // Scan helper
  const handleScan = async () => {
    resetNavGuard()
    await handleQrScan({ navigation: navigate })
    // handleQrScan already navigates; nothing else to do
  }

  return (
    <div className="flex flex-col gap-6 p-4 h-full text-foreground w-full">
      <TitleBar title="" />
      <div>
        <h3 className="text-lg font-semibold">Send Money</h3>
        <p className="text-sm text-neutral-600">Enter a Cray ID, address, ENS, or search a name</p>
      </div>

      {/* Unified input with both icons inside */}
      <div className="flex items-center w-full">
        <div className="relative w-full">
          <Input
            placeholder="Enter Cray ID, address, or name"
            value={value}
            onChange={(e) => {
              resetNavGuard()
              setValue(e.target.value)
            }}
            className="pr-16"
          />
          <div className="absolute right-3 top-4 flex items-center gap-3">
            <Clipboard className="h-5 w-5 cursor-pointer text-neutral-500 hover:text-primary" onClick={handlePaste} />
            <QrCode className="h-5 w-5 cursor-pointer text-neutral-500 hover:text-primary" onClick={handleScan} />
          </div>
        </div>
      </div>

      <p className="text-xs text-neutral-500">
        Tip: Paste an address, type a Cray ID (like <span className="font-mono">name@cray</span>), or scan a QR.
      </p>

      {/* When not auto-resolving: show contacts search */}
      <PeopleSearch
        query={value}
        onSelect={(c) => {
          didNavigate.current = true
          navigate('/dashboard/send-money', { state: c })
        }}
        showRecents
      />
    </div>
  )
}

export default SendAnyone
